import { ChangeDetectorRef, Component, OnInit } from '@angular/core';

import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';

import { NavigationInfo } from '../../_models/navigation-info';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';

import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-property-banner',
  templateUrl: './property-banner.component.html',
  styleUrls: ['./property-banner.component.scss'],
  standalone: false
})
export class PropertyBannerComponent implements OnInit {
  userProperties: UserPropertyResultDto[] = [];
  filteredProperties: UserPropertyResultDto[] = [];
  selectedProperty: UserPropertyResultDto = null;
  panelVisible: boolean = false;
  searchText: string = '';
  isSmallScreen: boolean = false;
  isInternal: boolean = true;
  propertyLength: number = 0;
  navigationInfo: NavigationInfo;
  sideNavExpanded: boolean = false;
  isLoadingProperties: boolean = false;

  constructor(
    private userService: UserService,
    private helperService: HelperService,
    private screenService: ScreenService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateScreenSize();
    this.getUserProperties(true);
    // Initialize sideNavExpanded based on screen size (sidebar is open by default on large screens)
    this.sideNavExpanded = this.screenService.sizes['screen-large'];

    RxjsUtils.safeSubscribe(this.screenService.changed, () => {
      this.updateScreenSize();
    });

    RxjsUtils.safeSubscribe(
      this.helperService.subjectSideNavSizeChanged.asObservable(),
      (expanded) => {
        this.sideNavExpanded = !!expanded;
        // On mobile, collapse the property dropdown when sidebar expands
        if (this.isSmallScreen && this.sideNavExpanded && this.panelVisible) {
          this.panelVisible = false;
        }
        // Force change detection to update banner width
        this.cdr.detectChanges();
      }
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectImpersonateChanged(),
      () => this.getUserProperties(true)
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectNewPropertyAdded(),
      () => this.getUserProperties(true)
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      (selectedProperty) => {
        this.selectedProperty = selectedProperty;
      }
    );

    RxjsUtils.subscribeToSubject(
      this.helperService.subjectSelectProperty,
      (info: NavigationInfo) => {
        this.navigationInfo = info;
        if (!!this.navigationInfo) {
          this.processNavigationInfo();
        }
      }
    );
  }

  togglePanel(): void {
    this.panelVisible = !this.panelVisible;
    if (this.panelVisible) {
      this.searchText = '';
      this.filteredProperties = [...this.userProperties];
      // On mobile, collapse the sidebar when dropdown opens
      if (this.isSmallScreen && this.sideNavExpanded) {
        this.helperService.subjectCloseSideNav.next();
      }
    }
  }

  onSearchChanged(event: any): void {
    const searchValue = (event.value || '').toLowerCase().trim();
    if (!searchValue) {
      this.filteredProperties = [...this.userProperties];
    } else {
      this.filteredProperties = this.userProperties.filter(
        (property) =>
          property.communityName?.toLowerCase().includes(searchValue) ||
          property.propertyAddress?.toLowerCase().includes(searchValue) ||
          property.customerId?.toLowerCase().includes(searchValue)
      );
    }
  }

  selectProperty(property: UserPropertyResultDto): void {
    if (property) {
      this.selectedProperty = property;
      this.userService.updateSubjectPropertySelectionChanged(
        this.selectedProperty
      );
      this.panelVisible = false;
    }
  }

  private getUserProperties(clearCache: boolean = false): void {
    this.userProperties = [];
    this.selectedProperty = null;
    this.propertyLength = 0;

    const userInfo = this.userService.getCurrentUserInfo();
    this.isInternal = userInfo?.isInternal ?? true;

    if (!this.isInternal) {
      this.isLoadingProperties = true;
      RxjsUtils.safeSubscribe(
        this.userService.getUserProperties(clearCache),
        (properties) => {
          this.isLoadingProperties = false;
          if (properties && properties.length) {
            this.userProperties = properties;
            this.propertyLength = this.userProperties.length;

            if (!!this.navigationInfo) {
              this.processNavigationInfo();
            } else {
              if (this.propertyLength > 1) {
                const impersonatedAccountNo =
                  this.userService.impersonatedAccountNo();
                const selectIndex =
                  !!impersonatedAccountNo && !!impersonatedAccountNo.length
                    ? this.userProperties.findIndex(
                        (x) => x.customerId === impersonatedAccountNo
                      )
                    : -1;

                if (selectIndex !== -1) {
                  this.selectedProperty = this.userProperties[selectIndex];
                  this.userService.updateSubjectPropertySelectionChanged(
                    this.selectedProperty
                  );
                } else {
                  const selectedProperty =
                    this.userService.getSelectedProperty();
                  if (!!selectedProperty) {
                    this.selectedProperty = selectedProperty;
                    this.userService.updateSubjectPropertySelectionChanged(
                      this.selectedProperty
                    );
                  } else {
                    this.userService.updateSubjectSelectDefaultProperty(
                      this.userProperties
                    );
                  }
                }
              } else {
                this.selectedProperty = this.userProperties[0];
                this.userService.updateSubjectPropertySelectionChanged(
                  this.selectedProperty
                );
              }
            }
            this.userService.updateSubjectNoProperties(true);
          } else {
            this.userService.updateSubjectNoProperties(false);
            this.userService.showAddPropertyModal();
          }
        }
      );
    } else {
      this.userService.updateSubjectNoProperties(false);
    }
  }

  private processNavigationInfo(): void {
    if (!!this.userProperties && !!this.userProperties.length) {
      const selectedProperty = this.userProperties.find((property) => {
        if (
          !!this.navigationInfo.communityId &&
          !!this.navigationInfo.customerId
        ) {
          return (
            this.navigationInfo.communityId ===
              property.communityId.toString() &&
            this.navigationInfo.customerId === property.customerId
          );
        } else if (!!this.navigationInfo.customerId) {
          return this.navigationInfo.customerId === property.customerId;
        } else if (!!this.navigationInfo.communityId) {
          return (
            this.navigationInfo.communityId === property.communityId.toString()
          );
        }
        return false;
      });

      if (!!selectedProperty) {
        this.selectedProperty = selectedProperty;
        this.userService.updateSubjectPropertySelectionChanged(
          this.selectedProperty
        );
      }
      this.navigationInfo = undefined;
    }
  }

  trackByUniqueId(_: number, property: UserPropertyResultDto): any {
    return property.uniqueId;
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }
}
